/**
 * YouTubeExtension — Custom TipTap node for embedded YouTube videos.
 *
 * Renders as a responsive 16:9 iframe embed. Accepts a YouTube URL
 * (full or short format), extracts the video ID, and renders the embed.
 *
 * Attributes: src (YouTube URL), videoId (extracted), caption (optional)
 */

import { Node, mergeAttributes } from '@tiptap/react';

export interface YouTubeOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    youtube: {
      setYouTube: (options: { src: string; caption?: string }) => ReturnType;
    };
  }
}

/** Extract YouTube video ID from various URL formats. */
function extractVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/, // bare video ID
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

export const YouTube = Node.create<YouTubeOptions>({
  name: 'youtube',
  group: 'block',
  content: '',
  draggable: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      src: { default: null },
      videoId: { default: null },
      caption: { default: '' },
    };
  },

  parseHTML() {
    return [
      { tag: 'div[data-youtube-video]' },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { videoId, caption, ...rest } = HTMLAttributes;
    const children: any[] = [
      ['iframe', mergeAttributes(rest, {
        src: `https://www.youtube.com/embed/${videoId}`,
        frameborder: '0',
        allowfullscreen: 'true',
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
        style: 'width:100%;aspect-ratio:16/9;border-radius:8px;',
      })],
    ];
    if (caption) {
      children.push(['figcaption', {}, caption]);
    }
    return ['div', mergeAttributes(this.options.HTMLAttributes, { 'data-youtube-video': '' }), ...children];
  },

  addCommands() {
    return {
      setYouTube:
        (options) =>
        ({ commands }) => {
          const videoId = extractVideoId(options.src);
          if (!videoId) return false;
          return commands.insertContent({
            type: this.name,
            attrs: { src: options.src, videoId, caption: options.caption || '' },
          });
        },
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentAttrs = { ...node.attrs };

      const dom = document.createElement('div');
      dom.setAttribute('data-youtube-video', '');
      dom.style.margin = '16px 0';
      dom.style.position = 'relative';

      // Top bar: label + delete button
      const topBar = document.createElement('div');
      topBar.style.cssText =
        'display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;';

      const label = document.createElement('div');
      label.style.cssText =
        'font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;' +
        'color:var(--text-muted);display:flex;align-items:center;gap:6px;';
      label.innerHTML = '<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:#dc2626;"></span>YouTube';
      topBar.appendChild(label);

      // Delete button
      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.textContent = '\u2715';
      deleteBtn.title = 'Remove video';
      deleteBtn.style.cssText =
        'background:rgba(220,38,38,0.1);border:1px solid rgba(220,38,38,0.25);border-radius:4px;' +
        'color:#dc2626;font-size:11px;width:22px;height:22px;cursor:pointer;display:flex;' +
        'align-items:center;justify-content:center;padding:0;line-height:1;transition:background 0.15s;';
      deleteBtn.addEventListener('mouseenter', () => { deleteBtn.style.background = 'rgba(220,38,38,0.2)'; });
      deleteBtn.addEventListener('mouseleave', () => { deleteBtn.style.background = 'rgba(220,38,38,0.1)'; });
      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        const resolvedNode = editor.view.state.doc.nodeAt(pos);
        if (!resolvedNode) return;
        editor.view.dispatch(
          editor.view.state.tr.delete(pos, pos + resolvedNode.nodeSize)
        );
        editor.view.focus();
      });
      topBar.appendChild(deleteBtn);

      dom.appendChild(topBar);

      // Responsive iframe wrapper
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:relative;width:100%;aspect-ratio:16/9;border-radius:8px;overflow:hidden;background:#000;';

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${currentAttrs.videoId}`;
      iframe.frameBorder = '0';
      iframe.allowFullscreen = true;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
      wrapper.appendChild(iframe);
      dom.appendChild(wrapper);

      // Helper: update attribute
      function setAttr(key: string, value: string) {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        const resolvedNode = editor.view.state.doc.nodeAt(pos);
        if (!resolvedNode) return;
        editor.view.dispatch(
          editor.view.state.tr.setNodeMarkup(pos, undefined, {
            ...resolvedNode.attrs,
            [key]: value,
          })
        );
      }

      // Caption input
      const captionInput = document.createElement('input');
      captionInput.type = 'text';
      captionInput.placeholder = 'Video caption…';
      captionInput.value = currentAttrs.caption || '';
      captionInput.style.cssText =
        'width:100%;border:none;outline:none;background:transparent;font-size:13px;font-style:italic;' +
        'color:var(--text-secondary);padding:6px 0 2px;font-family:inherit;box-sizing:border-box;';
      captionInput.addEventListener('blur', () => {
        if (captionInput.value !== currentAttrs.caption) {
          setAttr('caption', captionInput.value);
        }
      });
      dom.appendChild(captionInput);

      return {
        dom,
        stopEvent: (event: Event) => {
          const target = event.target;
          if (target === captionInput || target === iframe || target === deleteBtn) return true;
          return false;
        },
        ignoreMutation: () => true,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'youtube') return false;
          currentAttrs = { ...updatedNode.attrs };
          iframe.src = `https://www.youtube.com/embed/${currentAttrs.videoId}`;
          if (document.activeElement !== captionInput && captionInput.value !== currentAttrs.caption) {
            captionInput.value = currentAttrs.caption || '';
          }
          return true;
        },
      };
    };
  },
});
